import axios from 'axios';
import ConversationService from '@/services/ConversationService.js';
import TemplatesServices from '@/services/TemplatesService.js';
import ChatService from '@/services/ChatService.js';
import { encryptStorage } from '@/utils/storage.js';
import { isTabActive } from '@/utils/tabAttention.js';
import { serverUrl } from '@/config.js';
import { processSipmovilMessages } from '@/store/modules/chat.js'
import { buildStickerFile, isStickerFileName } from '@/modules/pbxCloud/components/Chat/emoji-picker/stickers.js';
import {
  wpDialogTime,
  getGupchupTypeMessage,
  wpMessageTime,
  getFileType,
} from '@/utils/utils';

// Texto de la notificación del navegador para un mensaje de SipMóvil: los
// adjuntos se nombran por su tipo (mismas etiquetas que el listado de chats)
// en vez de mostrar la URL del archivo.
function sipmovilNotificationBody(message) {
  if (!message.file_message) return message.message;
  if (isStickerFileName(message.file_message)) return 'Sticker';
  const fileObj = getFileType(message.file_message.split('.').at(-1));
  if (fileObj.isImage) return 'Imagen';
  if (fileObj.isAudio) return 'Audio';
  if (fileObj.isVideo) return 'Video';
  return 'Archivo';
}


const transferAudio = document.getElementById('sip-audio-transfer');
const newMessageAudio = document.getElementById('sip-audio-message');

export function processMessages(messages) {
  for (let index = 0; index < messages.length; index++) {
    const messageData = messages[index];
    messages[index].data.reverse();
    messages[index].time = wpMessageTime(
      `${messages[index].time}T00:00:00-05:00`
    );
    for (
      let indexMessage = 0;
      indexMessage < messageData.data.length;
      indexMessage++
    ) {
      const message = messageData.data[indexMessage];
      if (message.payload.text) {
        messages[index].data[indexMessage].message = message.payload.text;
      } else {
        messages[index].data[indexMessage].message = message.payload.previewUrl;
      }
      if (message.payload.caption) {
        messages[index].data[indexMessage].message = message.payload.caption;
      }
    }
  }
  return messages;
}


// Reacciones entrantes de join (WhatsApp/Gupshup): no llegan como `reactions` del
// mensaje, sino como mensajes sueltos type_message 'reaction' con
// payload { id, gsId, emoji }. payload.id apunta al id_whatsapp y payload.gsId al
// id_gupshup del mensaje reaccionado (mismo cruce que hace el backend con los
// webhooks de estado). emoji '' = el contacto retiró su reacción.
// Las reacciones que enviamos nosotros (agente) tienen otra forma:
// payload { type: 'reaction', msgId, emoji }, donde msgId es el id_gupshup (mensaje
// saliente) o el id_whatsapp (mensaje entrante) del mensaje reaccionado.
// La cuenta de WhatsApp tiene UNA reacción por mensaje, la comparten los agentes.
export const JOIN_BUSINESS_REACTION_ID = 'join-business';

// msgId para reaccionar a un mensaje de join: su wamid. WhatsApp solo permite
// reaccionar a mensajes RECIBIDOS del contacto; sobre los salientes Meta responde
// (#131009) Invalid message_id, así que para ellos no hay id → ''.
export function joinReactionTargetId(message) {
  if (!message || !message.source) return '';
  return message.id_whatsapp || '';
}

function isJoinReaction(message) {
  if (!message || !message.payload) return false;
  if (message.type_message === 'reaction') return true;
  if (message.payload.type === 'reaction') return true;
  // El evento 'newMessage' del socket no trae type_message: se reconoce por la
  // forma del payload. Reacción: { id, gsId, emoji }; reacción quitada: el mismo
  // payload sin emoji ({ id: <wamid> } sobre entrantes, { id, gsId } sobre salientes).
  if (message.type_message !== undefined) return false;
  const p = message.payload;
  const keys = Object.keys(p);
  const isRemoval =
    keys.length > 0 &&
    keys.every((k) => k === 'id' || k === 'gsId') &&
    !!(p.id || p.gsId);
  return (
    isRemoval ||
    ('emoji' in p && !!(p.id || p.gsId) && !p.text && !p.url && !p.caption)
  );
}

// Registra la reacción en state.joinReactions (una por contacto y mensaje destino;
// gana la más reciente). Se guardan aparte porque, al paginar, la reacción puede
// cargarse antes que el mensaje al que apunta (que queda en una página más vieja).
function registerJoinReaction(state, message) {
  const p = message.payload;
  const ts = new Date(message.created_at).getTime() || 0;
  let id, gsId, sender, username;
  if (p.msgId) {
    // Saliente: msgId puede ser id_gupshup o id_whatsapp, se cruza contra ambos.
    id = gsId = p.msgId;
    sender = JOIN_BUSINESS_REACTION_ID;
    username = message.owner_message || message.owner || null;
  } else {
    id = p.id || '';
    gsId = p.gsId || '';
    // source (teléfono) primero: viene igual en historial y socket; sender (id) para
    // contactos solo-username sin teléfono (FEX-1265).
    sender = String(message.source || message.sender || '');
    username = null;
  }
  const emoji = p.emoji || '';
  if (!id && !gsId) return;
  const prev = state.joinReactions.find(
    (r) => r.sender === sender && ((id && r.id === id) || (gsId && r.gsId === gsId))
  );
  if (!prev) {
    state.joinReactions.push({ id, gsId, sender, emoji, ts, username });
  } else if (ts >= prev.ts) {
    Object.assign(prev, { id: id || prev.id, gsId: gsId || prev.gsId, emoji, ts, username });
  }
}

// Saca los mensajes de reacción del historial (no se pintan como burbuja) y
// vuelca las reacciones registradas sobre su mensaje destino, con la misma forma
// plana que usa MessageReactions: [{ emoji, user_id, username }].
function applyJoinReactions(state) {
  for (const day of state.messages) {
    const reactionMsgs = day.data.filter(isJoinReaction);
    reactionMsgs.forEach((m) => registerJoinReaction(state, m));
    if (reactionMsgs.length) day.data = day.data.filter((m) => !isJoinReaction(m));
  }
  state.messages = state.messages.filter((day) => day.data.length > 0);
  if (!state.joinReactions.length) return;
  for (const day of state.messages) {
    for (const msg of day.data) {
      if (!msg) continue;
      const matches = state.joinReactions.filter(
        (r) =>
          (r.id && r.id === msg.id_whatsapp) ||
          (r.gsId && r.gsId === msg.id_gupshup)
      );
      if (!matches.length) continue;
      // La reacción y su retiro pueden apuntar al mensaje con ids distintos
      // (gsId vs wamid), así que se resuelve aquí: la más reciente por contacto.
      const latestBySender = {};
      for (const r of matches) {
        const prev = latestBySender[r.sender];
        if (!prev || r.ts >= prev.ts) latestBySender[r.sender] = r;
      }
      msg.reactions = Object.values(latestBySender)
        .filter((r) => r.emoji)
        .sort((a, b) => a.ts - b.ts)
        .map((r) => ({
          emoji: r.emoji,
          user_id: r.sender === JOIN_BUSINESS_REACTION_ID ? r.sender : `join:${r.sender}`,
          username: r.username,
        }));
    }
  }
}

async function getActivePbxUsernames() {
  try {
    const resp = await ChatService.getCompanyUsers();
    const users = resp?.data?.results || resp?.data || [];
    return new Set(users.map((u) => u.username));
  } catch (error) {
    console.log(error);
    return null;
  }
}

const conversation = {
  namespaced: true,
  state: {
    messages: [],
    joinReactions: [], // reacciones entrantes de join, ver registerJoinReaction
    statusMessages: [],
    isLoading: true,
    currentMessage: '',
    currentMessageTemplate: '',
    templateProperties: {},
    page: 1,
    remainMessages: null,
    transferAgents: [],
    transferByGroupAgents: [],
    transferGroupedAgents: [],
    gettingMessagesByFilter: false,
    retrySipmovilMessage: false,
    retrySipmovilMessageId: '',
    sendingMessage: 0, // contador: puede haber varios envíos join en vuelo a la vez
    sendingSipmovilMessage: false,
    messagesQueue: [],
    messagePlaceHolder:{},
    placeholderBackend:{},
    showMessagePlaceHolder: false,
    idTemplatePbx: '',
    paramsTemplatePbx:{},
    imageUrlTemplate: '',
    dialogForward: false,
    messageForward: '',
    isLoadingVariablesTemplates: false,
    isLoadingDefaultMessages: false,
    fexIAReceivingResponse: false,
    forwardSelectionMode: false,
    selectedForwardMessages: [],
    deleteSelectionMode: false,
    selectedDeleteMessages: [],
    copySelectionMode: false,
    selectedCopyMessages: [],
    successfullySent: false,
    membershipPeriods: null,
    isActiveMember: true,
    exitReason: null,
    // Leído aplazado: { chatId, User_id } que no se envió porque la pestaña no estaba
    // activa (oculta o sin foco). Se envía al volver a ella (flushPendingRead).
    pendingRead: null,
  },
  mutations: {
    SET_PENDING_READ(state, data) {
      state.pendingRead = data;
    },
    LOAD_MESSAGES(state, messages) {
      messages.reverse();
      for (let index = 0; index < messages.length; index++) {
        if (
          state.messages.length !== 0 &&
          messages[index].time === state.messages[0].time
        ) {
          const lastDay = messages.splice(index, 1)[0];
          // console.log(messages, state.messages, lastDay);
          state.messages[0].data = lastDay.data.concat(state.messages[0].data);
          // console.log(state.messages[0]);
        }
      }
      // const lastDay = messages.shift();
      // console.log(messages, state.messages, lastDay);
      // if (
      //   state.messages.length !== 0 &&
      //   lastDay.time === state.messages[0].time
      // ) {
      //   state.messages[0].data = lastDay.data.concat(state.messages[0].data);
      //   console.log(state.messages[0]);
      // } else {
      //   messages.unshift(lastDay);
      // }
      state.messages = messages.concat(state.messages);
      applyJoinReactions(state);
    },
    // Reacción entrante de join por socket: se pliega sobre el mensaje destino.
    ADD_JOIN_REACTION(state, message) {
      registerJoinReaction(state, message);
      applyJoinReactions(state);
    },
    LOAD_SIPMOVIL_MESSAGES(state, messages) {
      messages.reverse();
      for (let index = 0; index < messages.length; index++) {
        if (
          state.messages.length !== 0 &&
          state.messages[0].time === messages[index].time
          ) {
            const lastDay = messages.splice(index, 1)[0];
            state.messages[0].data = lastDay.data.concat(state.messages[0].data);
        }
      }
      state.messages = messages.concat(state.messages);
    },
    LOAD_SIPMOVIL_MESSAGES_FROM(state, {messages, reset}) {
      if (reset === true) {
        state.messages = []
      }
      messages.reverse();
      for (let index = 0; index < messages.length; index++) {
        if (
          state.messages.length !== 0 &&
          state.messages[0].time === messages[index].time
          ) {
            const lastDay = messages.splice(index, 1)[0];
            state.messages[0].data = lastDay.data.concat(state.messages[0].data);
        }
      }
      state.messages = messages.concat(state.messages);
    },
    NEW_MESSAGE(state, message) {
      if (state.messages[state.messages.length - 1].time === 'Hoy') {
        state.messages[state.messages.length - 1].data.push(message);
      } else {
        state.messages.push({
          time: 'Hoy',
          data: [message],
        });
      }
    },
    NEW_SIPMOVIL_MESSAGE(state, message) {
      if (
        state.messages.length > 0 &&
        state.messages[state.messages.length - 1].time === 'Hoy' &&
        !state.messages[state.messages.length - 1].data.includes(message)
      ) {
        state.messages[state.messages.length - 1].data.push(message);
      } else if (
        state.messages.length > 0 && 
        !state.messages[state.messages.length - 1].data.includes(message)
      ) {
          state.messages.push({
          time: 'Hoy',
          data: [message],
        });
      } else if (state.messages.length === 0) {
        state.messages.push({
          time: 'Hoy',
          data: [message],
        });
      }
    },
    SET_STATUS(state, status) {
      state.isLoading = status;
    },
    SET_ID_PBX_TEMPLATE(state, status) {
      state.idTemplatePbx = status
    },
    SET_PARAMS_TEMPLATE_PBX(state, status) {
      state.paramsTemplatePbx = status
    },
    SET_IMAGE_PBX_TEMPLATE(state, status) {
      state.imageUrlTemplate = status
    },
    UPDATE_MESSAGE(state, message) {
      state.currentMessage = message;
    },
    UPDATE_MESSAGE_TEMPLATE(state, message) {
      state.currentMessageTemplate = message;
    },
    APPEND_MESSAGE(state, message) {
      state.currentMessage += message;
    },
    ADD_MESSAGE(state, message) {
      const date = new Date();
      message.created_at = date.toISOString();
      if (
        state.messages.length > 0 &&
        state.messages[state.messages.length - 1].time === 'Hoy'
      ) {
        state.messages[state.messages.length - 1].data.push(message);
      } else {
        state.messages.push({
          time: 'Hoy',
          data: [message],
        });
      }
    },
    // Fusiona campos sobre un mensaje ya reactivo (buscado por id en el último
    // grupo de día). Mutar el objeto crudo de la acción directamente no dispara
    // reactividad, por eso se pasa por acá en vez de asignar las props a mano.
    UPDATE_MESSAGE_FIELDS(state, { id, fields }) {
      if (state.messages.length === 0) return;
      const data = state.messages[state.messages.length - 1].data;
      const idx = data.findIndex((m) => m && m.id === id);
      if (idx !== -1) {
        Object.assign(data[idx], fields);
      }
    },
    UPDATE_PAGE(state, pageNumber) {
      state.page = pageNumber;
    },
    CLEAN_MESSAGES(state) {
      state.messages = [];
      state.joinReactions = [];
      state.membershipPeriods = null;
      state.isActiveMember = true;
      state.exitReason = null;
    },
    SET_MEMBERSHIP_PERIODS(state, periods) {
      state.membershipPeriods = periods;
    },
    SET_ACTIVE_MEMBER(state, value) {
      state.isActiveMember = value;
    },
    SET_EXIT_REASON(state, value) {
      state.exitReason = value;
    },
    UPDATE_MESSAGE_READED_BY(state, { readerId, currentUsername }) {
      for (const dayGroup of state.messages) {
        for (const message of dayGroup.data) {
          if (!message || message._isSeparator || message.message_type === 'SYSTEM') continue;
          if (!Array.isArray(message.author) || message.author[0] !== currentUsername) continue;
          if (!Array.isArray(message.readed_by)) message.readed_by = [];
          if (!message.readed_by.includes(readerId)) {
            message.readed_by.push(readerId);
          }
        }
      }
    },
    // FEX-1124: fija `readedMsj = true` (sticky) en mis mensajes cuyo `readed_by` ya cubre a
    // TODOS los participantes activos. Así el "leído por todos" logrado EN VIVO no se pierde si
    // luego se agrega un miembro nuevo (el cómputo por readed_by compara vs miembros actuales y
    // revertiría; el booleano no). Replica lo que el backend devuelve al recargar.
    MARK_ALL_READ_STICKY(state, { activeIds, currentUsername }) {
      if (!Array.isArray(activeIds) || activeIds.length === 0) return;
      for (const dayGroup of state.messages) {
        for (const message of dayGroup.data) {
          if (!message || message._isSeparator || message.message_type === 'SYSTEM') continue;
          if (message.readedMsj === true) continue;
          if (!Array.isArray(message.author) || message.author[0] !== currentUsername) continue;
          const rb = (message.readed_by || []).map(String);
          if (activeIds.every((id) => rb.includes(id))) {
            message.readedMsj = true;
          }
        }
      }
    },
    SET_AGENTS(state, agents) {
      state.transferAgents = agents;
    },
    SET_AGENTS_GROUP(state, agents) {
      state.transferByGroupAgents = agents;
    },
    SET_AGENTS_GROUPED(state, groups) {
      state.transferGroupedAgents = groups;
    },
    UPDATE_MESSAGE_STATE(state, message) {
      if (state.messages.length === 0) return;
      const data = state.messages[state.messages.length - 1].data;
      const index = data.findIndex((x) => x.id_gupshup === message.id_gupshup);
      if (index === -1) return;
      data[index].status = message.status;
      // Gupshup a veces solo manda el webhook "read" del último mensaje de una
      // tanda leída de un tirón: si el más reciente ya se leyó, los propios
      // anteriores que seguían en 'sent'/'delivered' se dan por leídos también.
      if (message.status === 'read') {
        for (const day of state.messages) {
          for (const m of day.data) {
            if (m && m.source === '' && (m.status === 'sent' || m.status === 'delivered')) {
              m.status = 'read';
            }
          }
        }
      }
    },
    READ_LAST_MESSAGE(state) {
      const data = state.messages[state.messages.length - 1].data;
      data[data.length - 1].readedMsj = true;
      data[data.length - 1].status = "read";
    },
    SET_GETTING_MESSAGES_BY_FILTER(state, value) {
      state.gettingMessagesByFilter = value;
    },
    UPDATE_SIPMOVIL_MESSAGE_PARAM(state, { param, id, value }) {
      if (state.messages.length > 0) {
        const index = state.messages[state.messages.length - 1].data.findIndex(
          (x) => x.id === id
        );
        if (index !== -1) {
          state.messages[state.messages.length - 1].data[index][param] =
            value;
        }
      }
    },
    POP_SIPMOVIL_MESSAGE(state, { id, message }) {
      if (state.messages.length > 0) {
        const index = state.messages[state.messages.length - 1].data.findIndex(
          (x) => x.id == id
        );
        console.log('POP_SIPMOVIL_MESSAGE', index, id, message);
        if (index !== -1) {
          state.messages[state.messages.length - 1].data.splice(index, 1, message)
        }
      }
    },
    // FEX-1124: confirmación de envío (✓). Busca el bubble por su id temporal en
    // todos los grupos de día, lo marca 'sent' y reemplaza el id temporal por el
    // id real de BD (necesario para editar/eliminar/responder después).
    SET_MESSAGE_SENT(state, { tempId, messageId }) {
      for (const day of state.messages) {
        const idx = day.data.findIndex((m) => m && m.id == tempId);
        if (idx !== -1) {
          const msg = day.data[idx];
          // No degradar un estado superior (delivered/read) a 'sent'.
          if (msg.status !== 'read' && msg.status !== 'delivered') msg.status = 'sent';
          if (messageId !== undefined && messageId !== null) msg.id = messageId;
          return;
        }
      }
    },
    // FEX-1124: el destinatario recibió el mensaje (✓✓). Acepta un id o un lote.
    // Se busca por el id REAL de BD (ya intercambiado por SET_MESSAGE_SENT).
    // Acumula los ids de `delivered_by` (grupos) y marca `delivered` en el mensaje, para
    // que el cómputo por-participante (iconStatus en grupos) pueda decidir. No degrada 'read'.
    SET_MESSAGE_DELIVERED(state, { ids, delivered, deliveredBy }) {
      const idSet = ids.map(String);
      const incoming = Array.isArray(deliveredBy) ? deliveredBy.map(Number) : [];
      const isDelivered = delivered !== false; // el evento message_delivered implica entregado
      for (const day of state.messages) {
        for (const msg of day.data) {
          if (msg && idSet.includes(String(msg.id)) && msg.status !== 'read') {
            if (incoming.length) {
              const cur = Array.isArray(msg.delivered_by) ? msg.delivered_by.map(Number) : [];
              msg.delivered_by = Array.from(new Set([...cur, ...incoming]));
            }
            if (isDelivered) msg.delivered = true;
            msg.status = 'delivered';
          }
        }
      }
    },
    // FEX-1124: el backend no logró almacenar el mensaje → indicador de error.
    SET_MESSAGE_FAILED(state, { tempId }) {
      for (const day of state.messages) {
        const idx = day.data.findIndex((m) => m && m.id == tempId);
        if (idx !== -1) {
          day.data[idx].status = 'failed';
          day.data[idx].failed_status_spanish = 'No se pudo enviar el mensaje.';
          return;
        }
      }
    },
    // task_reacciones: registro OPTIMISTA de la reacción del usuario actual.
    // Forma plana del backend: msg.reactions = [{ id?, emoji, user_id, username, updated_at? }]
    // (una fila por usuario, ordenada por created_at asc). Un usuario tiene UNA
    // reacción por mensaje. Espeja el comportamiento del backend:
    //   - mismo emoji que ya tenía → la quita (toggle-off)
    //   - emoji distinto → reemplaza el emoji de su fila (conserva la posición/orden)
    //   - sin reacción previa → agrega su fila al final
    // El evento de socket / la respuesta del POST reemplazan luego con la lista
    // autoritativa (SET_MESSAGE_REACTIONS). Sirve a módulo y widget (misma fuente).
    SET_MESSAGE_REACTION(state, { messageId, emoji, userId, username }) {
      if (userId === undefined || userId === null || !emoji) return;
      for (const day of state.messages) {
        const msg = day.data.find((m) => m && m.id == messageId);
        if (!msg) continue;
        if (!Array.isArray(msg.reactions)) msg.reactions = [];
        const idx = msg.reactions.findIndex((r) => String(r.user_id) === String(userId));
        if (idx !== -1) {
          if (msg.reactions[idx].emoji === emoji) {
            msg.reactions.splice(idx, 1); // mismo emoji → quitar (toggle-off)
          } else {
            msg.reactions[idx].emoji = emoji; // emoji distinto → reemplazar
          }
        } else {
          msg.reactions.push({ emoji, user_id: userId, username });
        }
        return;
      }
    },
    // task_reacciones: reemplaza la lista COMPLETA de reacciones de un mensaje con
    // la versión autoritativa del backend (respuesta del POST y evento socket
    // 'message_reaction'). No-op si el mensaje no está en la conversación abierta.
    SET_MESSAGE_REACTIONS(state, { messageId, reactions }) {
      const list = Array.isArray(reactions) ? reactions : [];
      for (const day of state.messages) {
        const msg = day.data.find((m) => m && m.id == messageId);
        if (!msg) continue;
        msg.reactions = list;
        return;
      }
    },
    RESTART_AGENTS_PAGES(state) {
      for (const agent of state.transferAgents) {
        agent.page = 1;
      }
    },
    SET_SHOW_PLACEHOLDERS(state, value) {
      state.showMessagePlaceHolder = value;
    },
    SET_PLACEHOLDERS(state, newPlaceholders) {
      // Hacer merge de los placeholders existentes con los nuevos
      // Mantiene los valores existentes a menos que se proporcione uno nuevo
      state.messagePlaceHolder = {
        ...state.messagePlaceHolder, // Valores existentes
        ...newPlaceholders           // Sobrescribe con los nuevos valores
      };
    },
    RESET_PLACEHOLDERS(state) {
      state.messagePlaceHolder = {};
    },
    SET_PLACEHOLDER_BACKEND(state, value) {
      state.placeholderBackend = value;
    },
    SET_DIALOG_FORWARD(state, value) {
      state.dialogForward = value;
    },
    SET_MESSAGE_FORWARD(state, value) {
      state.messageForward = value;
    },
    SET_IS_LOADING_VARIABLES_TEMPLATES(state, value) {
      state.isLoadingVariablesTemplates = value;
    },
    SET_IS_LOADING_DEFAULT_MESSAGES(state, value) {
      state.isLoadingDefaultMessages = value;
    },
    SET_FORWARD_SELECTION_MODE(state, value) {
      state.forwardSelectionMode = value;
      if (!value) state.selectedForwardMessages = [];
    },
    TOGGLE_FORWARD_MESSAGE(state, messageId) {
      const idx = state.selectedForwardMessages.indexOf(messageId);
      if (idx === -1) {
        state.selectedForwardMessages.push(messageId);
      } else {
        state.selectedForwardMessages.splice(idx, 1);
      }
    },
    CLEAR_FORWARD_SELECTION(state) {
      state.forwardSelectionMode = false;
      state.selectedForwardMessages = [];
    },
    
    SET_DELETE_SELECTION_MODE(state, value) {
      state.deleteSelectionMode = value;
      if (!value) state.selectedDeleteMessages = [];
    },
    TOGGLE_DELETE_MESSAGE(state, messageId) {
      const idx = state.selectedDeleteMessages.indexOf(messageId);
      if (idx === -1) {
        state.selectedDeleteMessages.push(messageId);
      } else {
        state.selectedDeleteMessages.splice(idx, 1);
      }
    },
    CLEAR_DELETE_SELECTION(state) {
      state.deleteSelectionMode = false;
      state.selectedDeleteMessages = [];
    },
    SET_COPY_SELECTION_MODE(state, value) {
      state.copySelectionMode = value;
      if (!value) state.selectedCopyMessages = [];
    },
    TOGGLE_COPY_MESSAGE(state, messageId) {
      const idx = state.selectedCopyMessages.indexOf(messageId);
      if (idx === -1) {
        state.selectedCopyMessages.push(messageId);
      } else {
        state.selectedCopyMessages.splice(idx, 1);
      }
    },
    CLEAR_COPY_SELECTION(state) {
      state.copySelectionMode = false;
      state.selectedCopyMessages = [];
    },
  },
  actions: {
    async fetchMessages({ state, commit }, chatId) {
      commit('SET_STATUS', true);
      return ConversationService.getMessages(chatId, state.page)
        .then((response) => {
          console.log('retriving message for page', state.page, response);
          commit('LOAD_MESSAGES', response.data.results);
          commit('SET_STATUS', false);
          if (response.data.next !== null) {
            state.page += 1;
            console.log(
              'There are more message to load next page : ',
              state.page
            );
          } else {
            console.log('No more message to load');
            state.page = 0;
          }
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    conversationLoading({ commit }, status) {
      commit('SET_STATUS', status);
    },
    async uploadFiles({ commit, rootState, dispatch, state }) {
      const join = encryptStorage.getItem('user').join
      const token = join.token;
       if (rootState.ui.attachments.fileList.length === 0) {
        dispatch(
          'ui/alert/displayAlert',
          {
            message: 'Error',
            description: 'Debe seleccionar un archivo para enviar',
            alertType: 'error',
          },
          { root: true }
        );
        return;
      }
      commit('ui/attachments/INIT_UPLOAD', null, { root: true });
      return ConversationService.uploadFiles(
        rootState.ui.attachments.fileList,
        token
      )
        .then(async function (e) {
          console.log('SUCCESS!!', e);
          const { urls } = e.data;
          // FEX-1315: una sola descripción por lote, en el último archivo (ver
          // uploadSipmovilFiles). Aquí además se limpia el composer, que antes
          // se quedaba con el texto y lo reenviaba en el siguiente lote.
          const captionText = state.currentMessage;
          commit('UPDATE_MESSAGE', '');
          for (let index = 0; index < urls.length; index++) {
            const { url } = urls[index];
            const { file } = rootState.ui.attachments.fileList.filter(
              (x) => x.file.name === urls[index].filename
            )[0];
            const payload = getGupchupTypeMessage(file, `${serverUrl}${url}`)
            // El resto conserva el caption por defecto (el nombre del archivo),
            // que es lo que hasUserCaption reconoce como "no escrito por el
            // usuario" y por eso no se renderiza como texto.
            if (index === urls.length - 1 && captionText) {
              payload.caption = captionText
            }
            await dispatch('sendOfficialMessage', {
              message: `${serverUrl}${url}`,
              chatId: rootState.chat.openedChat.chatId || rootState.chat.openedChat.id,
              payload,
            });
          }
          // reset attachement interface
          commit('ui/attachments/RESET_ATTACHEMENTS_UI', null, { root: true });
          return true;
        })
        .catch(function (e) {
          console.log('FAILURE!!', e);
          commit('ui/attachments/RESET_ATTACHEMENTS_UI', null, { root: true });
          dispatch(
            'ui/alert/displayAlert',
            {
              message: 'Error',
              description:
                'Ocurrio un error al enviar el archivo, por favor intentelo nuevamente',
              alertType: 'error',
            },
            { root: true }
          );
          return false;
        });
    },
    // Devuelve true si todos los archivos del lote se enviaron (false si alguno
    // falló o no había nada que enviar): la nota de voz lo usa para decidir si
    // conserva la grabación para reintentar.
    async uploadSipmovilFiles({ state, commit, dispatch, rootState }, data) {
      const host = rootState.auth_store.userInfo.PbxServer;
      const userName = encryptStorage.getItem('user').Username;
      const userId = encryptStorage.getItem('user').User_id;
      const token = encryptStorage.getItem("token");
      // El caption por defecto es lo que hay escrito en el composer, pero quien
      // llama puede imponer el suyo (los stickers lo mandan vacío para no
      // arrastrar el texto en curso). FEX-1192.
      const forcedCaption = data && typeof data.caption === 'string' ? data.caption : null;
      data = {
        username: userName,
        id: userId,
        chat_id: rootState.chat.openedChat.chatId,
        caption: forcedCaption !== null ? forcedCaption : state.currentMessage,
      }
      if (rootState.ui.attachments.fileList.length === 0) {
        dispatch(
          'ui/alert/displayAlert',
          {
            message: 'Error',
            description: 'Debe seleccionar un archivo para enviar',
            alertType: 'error',
          },
          { root: true }
        );
        return false;
      }
      const captionText = data.caption; // guardar antes de que UPDATE_MESSAGE lo limpie
      const isFexIAChat = rootState.chat.openedChat.isFexIA || false;
      commit('ui/attachments/INIT_UPLOAD', null, { root: true });
      // FEX-1315: la descripción es UNA sola para todo el lote. Viaja con el
      // último archivo (queda al pie del bloque, que es donde el chat ancla la
      // vista) y vacía en el resto, así el backend no la repite por archivo.
      // Copia de la lista porque RESET_ATTACHEMENTS_UI la vacía al terminar.
      const files = [...rootState.ui.attachments.fileList];
      // El composer se limpia de una (el texto ya está en captionText): si se
      // limpiara al final del lote pisaría lo que el usuario empiece a teclear.
      commit('UPDATE_MESSAGE', '');
      // Secuencial: así el orden de las burbujas es el orden en que se adjuntaron
      // y la descripción queda de verdad en el último mensaje del bloque.
      let failed = 0;
      try {
        for (let index = 0; index < files.length; index++) {
          const isLast = index === files.length - 1;
          const fileCaption = isLast ? captionText : '';
          try {
            const e = await ConversationService.uploadSipmovilFiles(
              files[index].file,
              host,
              token,
              { ...data, caption: fileCaption },
            );
            console.log('SUCCESS!!', e);
            const content = e.data.content;
            // Si el backend no devuelve el caption, lo inyectamos manualmente
            // desde el texto que escribió el usuario. Solo en el archivo que lo
            // llevaba: si no, el render optimista lo repetiría en todo el lote.
            if (isLast && content && content.message && !content.message.caption && fileCaption) {
              content.message.caption = fileCaption;
            }
            // FexIA: la IA no notifica entrega; el adjunto se marca 'delivered' apenas el
            // POST /attached responde OK (igual que el texto en sendSipmovilMessageAction,
            // que fija status='delivered' para fexIA). Así el adjunto muestra ✓✓, no ✓.
            if (isFexIAChat && content && content.message) {
              content.message.delivered = true;
            }
            dispatch('newSipmovilChatMessage', content);
            state.fexIAReceivingResponse = rootState.chat.openedChat.isFexIA || false;
          } catch (e) {
            // Un archivo que falla no corta el lote: se avisa y se sigue con el resto.
            console.log('FAILURE!!', e);
            failed++;
            dispatch(
              'ui/alert/displayAlert',
              {
                message: 'Error',
                description:
                  'Ocurrio un error al enviar el archivo, por favor intentelo nuevamente',
                alertType: 'error',
              },
              { root: true }
            );
            // Si falla justo el archivo que llevaba la descripción, el texto
            // vuelve al composer (si el usuario no escribió otra cosa) para no
            // perderlo: los demás archivos ya se fueron sin él.
            if (isLast && fileCaption && !state.currentMessage) {
              commit('UPDATE_MESSAGE', fileCaption);
            }
          }
        }
      } finally {
        // Cierre garantizado: si el lote se corta a mitad sin limpiar, quedan
        // archivos YA ENVIADOS cargados en la vista previa y el siguiente envío
        // los repite.
        commit('ui/attachments/RESET_ATTACHEMENTS_UI', null, { root: true });
      }
      return failed === 0;
    },
    // FEX-1192: envía un sticker del catálogo como adjunto. Reutiliza el mismo
    // pipeline que las notas de voz (File en memoria → APPEND_FILE → upload),
    // sin pasar por la vista previa. El caption va vacío y el borrador del
    // composer se conserva: el sticker no debe llevarse el texto en curso.
    async sendStickerAction({ state, commit, dispatch, rootState }, sticker) {
      if (rootState.chat.originOpenedChat !== 'sipmovil') return;
      // Hay archivos esperando en la vista previa: subir el sticker ahora los
      // arrastraría en el mismo lote.
      if (rootState.ui.attachments.fileList.length > 0) return;
      if (rootState.ui.attachments.uploadingFiles) return;
      const draft = state.currentMessage;
      try {
        const file = await buildStickerFile(sticker);
        commit('ui/attachments/APPEND_FILE', file, { root: true });
        await dispatch('uploadSipmovilFiles', { caption: '' });
        commit('UPDATE_MESSAGE', draft);
      } catch (error) {
        console.error('[stickers] no se pudo enviar el sticker:', error);
        commit('ui/attachments/RESET_ATTACHEMENTS_UI', null, { root: true });
        commit('UPDATE_MESSAGE', draft);
        dispatch(
          'ui/alert/displayAlert',
          {
            message: 'Error',
            description: 'No se pudo enviar el sticker, por favor intentelo nuevamente',
            alertType: 'error',
          },
          { root: true }
        );
      }
    },
    async sendMessage({ commit, rootState }, { message, chatId }) {
      const emojiVisible = rootState.ui.emojis.showEmojis;
      const userName = encryptStorage.getItem('user').Username;
      if (message) {
        const messageObject = {
          message,
          sender: 'Tú',
          messagesNumber: 1,
          contact_msg: chatId,
          response_message: false,
          owner_message: userName,
        };
        return ConversationService.saveMessage(messageObject)
          .then(() => {
            commit('UPDATE_MESSAGE', '');
            commit(
              'chat/UPDATE_LAST_MESSAGE',
              {
                chatId,
                messageContent: message,
                direction: 'out',
                messageDate: wpDialogTime(new Date(message.created_at)),
                noReply: rootState.auth.onlyNoReply,
                isFiltered: false,
              },
              { root: true }  
            );
            commit('ADD_MESSAGE', messageObject);
            if (emojiVisible === true) {
              commit('ui/emojis/TOGGLE_EMOJI', null, { root: true });
            }
            return 'Success';
          })
          .catch((error) => {
            console.log(error);
          });
      }
      return null;
    },
    async sendOfficialMessage({ state, commit, dispatch, rootState },{ message, chatId , payload }) {
      const emojiVisible = rootState.ui.emojis.showEmojis;
      const currentChat = rootState.chat.chats.filter(
        (x) => x.id === chatId
      )[0];
      currentChat.state = currentChat.state === 'closed' ? 'init' : currentChat.state;
      const join = encryptStorage.getItem('user').join
      const user = encryptStorage.getItem('user');
      const PbxServer = user.PbxServer;
      const userName = encryptStorage.getItem('user').Username
      let messageObject = {}
      if (message) {
        if(state.idTemplatePbx.length > 0) {
          messageObject = {
            user: join.adminUser,
            id: state.idTemplatePbx,
            destination: currentChat.destination,
            params: Object.values(state.paramsTemplatePbx).map(name => name).join(','),
            file_message: state.imageUrlTemplate ? 'https://' + PbxServer + state.imageUrlTemplate : '',
          };
        }else {
          messageObject = {
            destination: currentChat.destination,
            app_name: currentChat.app_name,
            owner_message: userName,
            payload,
          };
        }
        const renderMessageObject = {
          // Id temporal de front, se reemplaza por el id real de BD al responder el POST.
          id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          // Optimista: se pinta ya, sin esperar el POST. Si falla, el catch lo baja a 'failed'.
          status: 'sent',
          message:  payload.filename || payload.caption || message,
          sender: 'Tú',
          messagesNumber: 1,
          contact_msg: chatId,
          response_message: false,
          owner_message: userName,
          source: '',
          // Incluir el payload completo para que el render inicial tenga
          // disponible payload.filename (nombre original limpio) y payload.caption,
          // así NO se ve el nombre con hash que viene en la URL.
          payload,
        };
        if (payload.url || payload.previewUrl ) {
          renderMessageObject.file_message = message;
        }
        if (state.imageUrlTemplate) {
          renderMessageObject.file_message = 'https://' + PbxServer + state.imageUrlTemplate;
        }
        if (state.messages.length > 0) {
          const data = state.messages[state.messages.length - 1].data;
          if (data[data.length - 1].source) {
            dispatch("setAgentLastMessage", { owner: currentChat.owner, direction: 'out' });
          }
        }
        const token = join.tokenJoin;
        state.sendingMessage += 1;
        commit('ADD_MESSAGE', renderMessageObject);
        if(!state.idTemplatePbx) {
           return ConversationService.saveOfficialMessage(messageObject, token)
          .then((resp) => {
            console.log('respuesta message:', resp)
            // if (rootState.chat.foundJoinChats.length > 0 || rootState.chat.filteredChats) {
              // const newChats = rootState.ui.customerOptions.renderChats.filter((x) => x.id !== chatId);
              // commit('chat/SET_CHATS', [currentChat, ...newChats], { root: true });
              // commit('ui/customerOptions/SET_RENDER_CHATS', rootState.chat.chats, { root: true });
              // commit('chat/SET_JOIN_FILTERED_CHATS', [false, ''], { root: true });
              // commit('chat/UPDATE_FOUNDED_CHATS', [], { root: true });
            // } 
            commit(
              'chat/UPDATE_LAST_MESSAGE',
              {
                chatId,
                messageContent: message,
                direction: 'out',
                messageDate: wpDialogTime(new Date(new Date().getTime() - 1000)),
                currentChat: false,
                isFiltered: false,
              },
              { root: true }
            );
            dispatch('chat/updateUnread', chatId, { root: true });
            commit('UPDATE_MESSAGE_FIELDS', {
              id: renderMessageObject.id,
              fields: {
                id: resp.data.id,
                id_gupshup: resp.data.id_gupshup,
                // id_whatsapp_context: necesario para que la cita/reply se vea al instante.
                id_whatsapp_context: resp.data.id_whatsapp_context,
                id_whatsapp: resp.data.id_whatsapp,
              },
            });
            state.sendingMessage -= 1;
            if (emojiVisible === true) {
              commit('ui/emojis/TOGGLE_EMOJI', null, { root: true });
            }
            return 'Success';
          })
          .catch((error) => {
            commit('UPDATE_MESSAGE_FIELDS', {
              id: renderMessageObject.id,
              fields: { status: 'failed', failed_status_spanish: 'No se pudo enviar el mensaje.' },
            });
            state.sendingMessage -= 1;
            dispatch(
              'ui/alert/displayAlert',
              {
                message: 'Error',
                description:
                  'Ocurrio un error al enviar el mensaje, por favor intentelo nuevamente',
                alertType: 'error',
              },
              { root: true }
            );
            console.log(error);
            return 'Error';
          });
        } else {
           return ConversationService.saveOfficialMessagePbx(messageObject, token)
          .then((resp) => {
            console.log('respuesta del masivo:', resp)
            commit('SET_PARAMS_TEMPLATE_PBX', {});
            // if (rootState.chat.foundJoinChats.length > 0 || rootState.chat.filteredChats) {
              // const newChats = rootState.ui.customerOptions.renderChats.filter((x) => x.id !== chatId);
              // commit('chat/SET_CHATS', [currentChat, ...newChats], { root: true });
              // commit('ui/customerOptions/SET_RENDER_CHATS', rootState.chat.chats, { root: true });
              // commit('chat/SET_JOIN_FILTERED_CHATS', [false, ''], { root: true });
              // commit('chat/UPDATE_FOUNDED_CHATS', [], { root: true });
            // } 
            commit(
              'chat/UPDATE_LAST_MESSAGE',
              {
                chatId,
                messageContent: message,
                direction: 'out',
                messageDate: wpDialogTime(new Date(new Date().getTime() - 1000)),
                currentChat: false,
                isFiltered: false,
              },
              { root: true }
            );
            dispatch('chat/updateUnread', chatId, { root: true });
            // No tengo estos datos en la respuesta
            commit('UPDATE_MESSAGE_FIELDS', {
              id: renderMessageObject.id,
              fields: {
                id: resp.data.id,
                id_gupshup: resp.data.id_gupshup,
                file_message: resp.data.file_message,
                // El payload de respuesta trae la data real de la plantilla enviada
                // (template.params y buttons resueltos por backend), a diferencia del
                // payload local que solo tenía el texto plano armado en el front.
                ...(resp.data.payload ? { payload: resp.data.payload } : {}),
              },
            });
            state.sendingMessage -= 1;
            if (emojiVisible === true) {
              commit('ui/emojis/TOGGLE_EMOJI', null, { root: true });
            }
            return 'Success';
          })
          .catch((error) => {
            commit('UPDATE_MESSAGE_FIELDS', {
              id: renderMessageObject.id,
              fields: { status: 'failed', failed_status_spanish: 'No se pudo enviar el mensaje.' },
            });
            state.sendingMessage -= 1;
            dispatch(
              'ui/alert/displayAlert',
              {
                message: 'Error',
                description:
                  'Ocurrio un error al enviar el mensaje, por favor intentelo nuevamente',
                alertType: 'error',
              },
              { root: true }
            );
            console.log(error);
            return 'Error';
          });
        }
      }
      return null;
    },
    async sendSurveyMessage({ dispatch }, { chat, payload }) {
      const join = encryptStorage.getItem('user').join
      const userName = encryptStorage.getItem('user').Username
      const messageObject = {
        source: chat.destination,
        by_bot: true,
        app_name: chat.app_name,
        owner_message: userName,
        payload,
      };
      const token = join.tokenJoin;
      return ConversationService.saveSurveyMessage(messageObject, token)
        .then((resp) => {
          console.log(resp);
          return 'Success';
        })
        .catch((error) => {
          dispatch(
            'ui/alert/dispatch',
            {
              message: 'Error',
              description:
                'Ocurrio un error al enviar el mensaje, por favor intentelo nuevamente',
              alertType: 'error',
            },
            { root: true }
          );
          console.log(error);
          return 'Error';
        });
    },
    newMessage({ commit, dispatch, rootState }, message) {
      const currentChat = message.contact_msg === rootState.chat.openedChat.id;
      const noReply = rootState.auth.onlyNoReply;
      const isFiltered = rootState.chat.filteredChats ||
        noReply ||
        rootState.chat.filterUnread ||
        rootState.chat.stringAgents.length > 0 ||
        rootState.chat.stringLabels.length > 0;

      const chats = rootState.chat.chats
      const isNewChat = chats.filter((x) => x.id === message.contact_msg).length;
      if (message.payload) {
        if (message.payload.text) {
          message.message = message.payload.text;
        }
        if (message.payload.url) {
          message.message = message.payload.url;
        }
        if (message.file_message) {
          message.message = message.file_message.split("/").pop();
        }
        if (message.payload.caption) {
          message.message = message.payload.caption;
        }
      }
      const isReaction = isJoinReaction(message);
      if (isReaction) {
        message.message = message.payload.emoji
          ? `Reaccionó ${message.payload.emoji}`
          : 'Quitó su reacción';
      }
      let direction = 'in';
      if (String(message.destination) !== 'undefined') {
        direction = 'out';
        message.direction = 'out';
      } else if (
        message.source === undefined ||
        message.source === null ||
        message.source === ''
      ) {
        // Los mensajes de sistema llegan por socket SIN `source` ni `destination`.
        // Sin esta rama caían en el 'in' por defecto y se trataban como mensaje del
        // cliente: burbuja a la izquierda, tono de entrante y notificación de
        // escritorio. Al recargar no pasaba porque el REST sí trae los campos y
        // isOutput() (ConversationMessage.vue) los resuelve como salientes.
        // Un mensaje real del cliente siempre trae `source` (de hecho se usa como
        // teléfono al crear el chat unas líneas más abajo), así que la condición
        // sólo alcanza a los de sistema.
        direction = 'out';
        message.direction = 'out';
        message.source = '';
      }

      if ( direction === 'in' && rootState.auth_store.userInfo.audioJoin && !rootState.softphone_store.chatNotificationsMuted ) {
          newMessageAudio.play();
          const options = {
            body: message.message,
            icon: 'notification-logo.png',
          };
          new Notification(
            `SipMóvil Join\nMensaje de ${message.contact || message.sender_custom_name || message.sender_name}`,
            options
          );
      }
      if (isNewChat === 0) {
        console.log('[newMessage socket] origen del mensaje:', message.contact_msg, 'origin:', message.origin);
        // FEX-1265: contactos que escriben solo con "username" de WhatsApp no traen
        // source/destination (no hay telefono); se identifican por sender_bsuid.
        const contact = message.contact || message.sender_custom_name || message.sender_name || message.sender_username;
        const chatData = {
          id: message.contact_msg,
          last_message: message.message,
          last_message_direction: direction,
          date_message: message.created_at,
          name: contact,
          phone: direction === 'in' ? message.source : message.destination,
          bsuid: message.sender_bsuid,
          username: message.sender_username,
          img_dialog: message.img_dialog,
          is_group: message.is_group,
          owner: message.owner,
          unread: 1,
          state: message.state,
          writing: false,
          origin: message.origin || '',
        };
        dispatch('chat/fetchChat', [chatData], { root: true });
        const newChats = chats.filter((x) => x.id !== message.contact_msg);
        const chatMessage = rootState.chat.chats.filter((x) => x.id == message.contact_msg)[0];
        commit('chat/SET_CHATS', [chatMessage, ...newChats], { root: true });
      } else {
        const newChats = chats.filter((x) => x.id !== message.contact_msg);
        const chatMessage = chats.filter((x) => x.id == message.contact_msg)[0];
        chatMessage.state = message.state;
        commit('chat/SET_CHATS', [chatMessage, ...newChats], { root: true });
        const directionChat = chatMessage.last_message_direction;
        if (directionChat !== direction && direction === 'out' && chatMessage.unread === 0) {
          dispatch("setAgentLastMessage", { owner: message.owner, direction: direction });
        }
      }  
      
      const respData = {
        chatId: message.contact_msg,
        messageContent: message.message,
        direction,
        messageDate: wpDialogTime(new Date(message.created_at)),
        currentChat: currentChat,
        isFiltered: isFiltered,
      };
      
      if (currentChat) {
        commit(isReaction ? 'ADD_JOIN_REACTION' : 'NEW_MESSAGE', message);
      }
      commit('chat/UPDATE_LAST_MESSAGE', respData, { root: true });
      // if (!isFiltered) dispatch('chat/updateUnread', respData.chatId, { root: true });
    },
    newSipmovilChatMessage({ state, commit, dispatch, rootState }, data) {
      // const currentChat = data.chat_id == rootState.chat.openedChat.chatId;
      const user = encryptStorage.getItem('user');
      const userName = user.Username;
      const PbxServer = user.PbxServer;
      const message = data.message;
      if (state.messages.length > 0) {
        if(
          state.messages.at(-1).data.at(-1).id === message.id ||
          state.messages.at(-1).data.at(-1).message === message.message
        ) {
          return
        }
      }
      message.created_at = message.timestamp;
      // FEX-1124: read (✓✓ dorado) > delivered (✓✓ negro) > sent (✓). Aplica también a
      // adjuntos, que se renderizan por aquí desde la respuesta de POST /attached
      // (trae sent/delivered). El estado solo es visible en mensajes propios (isOutput).
      message.status = message.readedMsj
        ? 'read'
        : (message.delivered ? 'delivered' : 'sent')
      message.payload = {
        type: message.kind,
        text: message.content
      };
      if (!message.kind || message.kind === "NO_ATTACHED") {
        message.message = message.content;
      } else {
        message.file_message = message.url;
        message.message = message.url;
      }
      message.direction = 'in';
      if (message.author[0] === userName) {
        message.direction = 'out';
      }
      if (message.readed_by !== undefined && message.readed_by !== null) {
        if (!Array.isArray(message.readed_by)) {
          message.readed_by = String(message.readed_by).split(',').filter(Boolean).map(Number);
        }
      } else {
        message.readed_by = [];
      }
      // Reacciones (task_reacciones): garantizar el array reactions:[{emoji,users:[]}].
      if (!Array.isArray(message.reactions)) message.reactions = [];
      if (message.direction !== 'out') {
        if (rootState.auth_store.userInfo.audioSipmovil && !rootState.softphone_store.chatNotificationsMuted) {
          if (
            data.command !== 'new_call_message'
          ){
            // Solo emitir sonido y notificación si:
            // 1. El chat abierto es diferente al que envió el mensaje
            // 2. O la ventana no está enfocada
            const isSameChat = rootState.chat.openedChat.chatId === data.chat_id;
            const isWindowFocused = document.hasFocus();

            if (!isSameChat || !isWindowFocused) {
              newMessageAudio.play();
              const options = {
                body: sipmovilNotificationBody(message),
                icon: 'notification-logo.png',
              };
              const notification = new Notification(
                `SipMóvil Chat\nMensaje de ${data.message.author[1]}`,
                options
              );

              notification.onclick = () => {
                window.focus();
                const chatData = {
                    chatId: data.chat_id,
                    messageContent: message.message,
                };

                if (window.location.hash !== "#/pbxCloud/chat") {
                    window.location.hash = "#/pbxCloud/chat";
                }

                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent("openChatFromNotification", { detail: chatData }));
                }, 500);
              };
            }
          }
        }
      }
      const respData = {
        chatId: data.chat_id,
        messageContent: message.message,
        direction: message.direction,
        message: message,
        messageDate: wpDialogTime(new Date(message.created_at)),
      };
      // if (currentChat) {
        // }
      var chatIndex = rootState.chat.sipmovilChats.findIndex((x) => x.chatId == data.chat_id, -1);
      var chat = rootState.chat.sipmovilChats[chatIndex];
      
      if (rootState.chat.filteredSipmovilChats) {
        chatIndex = rootState.chat.renderSipmovilChats.findIndex((x) => x.chatId == data.chat_id, -1);
        chat = rootState.chat.renderSipmovilChats[chatIndex];
      }
      if (chat === undefined) {
        chat = {
          id: data.chat_id,
          chatId: data.chat_id,
          getInfoName: "",
          contact_dialog: data.name,
          last_message: "",
          last_message_status: 'unread',
          last_message_incoming: message.direction === 'in' ? true : false,
          date_message: "",
          img_dialog: `https://${PbxServer}/static/images/group.png`,
          unread: 0,
          isGroup: true,
          onlyAdminsMessages: data.onlyAdminsMessages,
          state: "online",
          chats: [],
        };
        if (data.contacts.length <= 2 && !data.is_group) {
          chat.contact_dialog = data.message.author[1];
          chat.img_dialog = `https://${PbxServer}${data.message.picture}`,
          chat.isGroup = false;
        }
        if (rootState.chat.filteredSipmovilChats) {
          commit('chat/CREATE_RENDER_SIPMOVIL_CHAT', chat, { root: true });
        } else {
          commit('chat/CREATE_SIPMOVIL_CHAT', chat, { root: true });
        }
      }
      commit('chat/UPDATE_SIPMOVIL_LAST_MESSAGE', respData, { root: true });
      dispatch('chat/updateUnreadSipmovil', null, { root: true });      
      if (rootState.chat.openedChat.chatId == data.chat_id) {
        commit('NEW_SIPMOVIL_MESSAGE', message);
      }
    },
    appendMessage({ state, commit, dispatch, rootState }, data) {
      const message = data.message
      if (state.messages.length > 0) {
        if (state.messages.at(-1).data.at(-1).id === message.id ||
          state.messages.at(-1).data.at(-1).message === message.message
          ) {
          return
        }
      }
      message.created_at = message.timestamp;
      // FEX-1124: eco de mi propio mensaje. read (✓✓ dorado) > delivered (✓✓ negro) > sent (✓).
      message.status = message.readedMsj
        ? 'read'
        : (message.delivered ? 'delivered' : 'sent')
      message.payload = {
        type: message.kind,
        text: message.content
      };
      if (!message.kind || message.kind === "NO_ATTACHED") {
        message.message = message.content;
      } else {
        message.file_message = message.url;
        message.message = message.url;
      }
      // El eco de MI propio mensaje es 'out'; pero por aquí también entra la respuesta
      // ENTRANTE de FexIA (branch fexIA_response). Derivamos la dirección del autor en vez
      // de forzar 'out', para que la respuesta del bot sea 'in' y NO muestre estatus (ni
      // check en la burbuja ni en el listado), igual que cualquier mensaje entrante.
      const currentUserName = encryptStorage.getItem('user').Username;
      message.direction =
        Array.isArray(message.author) && message.author[0] === currentUserName ? 'out' : 'in';
      // FexIA: mis mensajes se consideran ENTREGADOS (✓✓) porque la IA no notifica entrega.
      // El eco del backend trae delivered:false → sin esto, el mensaje caería de ✓✓ (optimista)
      // a ✓. Mantener 'delivered' para mensajes propios en chats FexIA (igual que el optimista
      // en sendSipmovilMessageAction que fija status='delivered' para fexIA_response).
      if (rootState.chat.openedChat.isFexIA && message.direction === 'out' && message.status !== 'read') {
        message.status = 'delivered';
      }
      // Reacciones (task_reacciones): garantizar el array reactions:[].
      if (!Array.isArray(message.reactions)) message.reactions = [];
      const respData = {
        chatId: data.chat_id,
        messageContent: message.message,
        direction: message.direction,
        messageDate: wpDialogTime(new Date(message.created_at)),
        message: message,
      };
      commit('chat/UPDATE_SIPMOVIL_LAST_MESSAGE', respData, { root: true });
      dispatch('chat/updateUnreadSipmovil', null, { root: true });
      if (rootState.chat.openedChat.chatId === data.chat_id) {
        // console.log('Llegando mensaje...', message);
        const index = state.messages[state.messages.length - 1].data.findIndex(
          (x) => x.id === data.platformId
        );
        if (index !== -1) {
          state.messages[state.messages.length - 1].data.splice(index, 1, message)
        } else {
          commit('NEW_SIPMOVIL_MESSAGE', message);
        }
      }
    },
    async sendSipmovilMessageAction({ state, commit, dispatch, rootState }, formData) {
      const maxRetries = 5; // Número máximo de intentos
      let attempts = 0;
      formData.direction = 'out';
      formData.status = 'enqueue';
      formData.deleted = false;
      formData.created_at = new Date();
      state.retrySipmovilMessage = true;
      state.retrySipmovilMessageId = formData.id;
      commit("NEW_SIPMOVIL_MESSAGE", formData);
      state.messagesQueue.push(formData);
      if (state.sendingSipmovilMessage) {
        return;
      }
      state.sendingSipmovilMessage = true;
      state.fexIAReceivingResponse = rootState.chat.openedChat.isFexIA || false;
      while(state.messagesQueue.length > 0) {
        const messageInQueue = state.messagesQueue.splice(0, 1)[0];
        state.successfullySent = false;
        while (attempts < maxRetries) {
          try {
            // console.log('Enviando mensaje...', messageInQueue);
            const response = await ConversationService.sendSipmovilMessage(messageInQueue)
            if (response.status === 201) {
              if (response.data.fexIA_response === true) {
                formData.status = 'delivered';
                // formData.chat_id = formData.chatId;
                response.data.chat_id = formData.chatId;
                // state.messages[state.messages.length - 1].data.splice(-1, 1);
                dispatch('appendMessage', response.data);
                dispatch('markAsReadMessages', {data: response.data, status: 'delivered'});
              } else if (!rootState.chat.openedChat.isFexIA) {
                commit('SET_MESSAGE_SENT', { tempId: messageInQueue.id, messageId: undefined });
              }
              // if (state.retrySipmovilMessage) {
              //   console.log('Enviando mensaje...', response);
              //   commit("POP_SIPMOVIL_MESSAGE", { id: messageInQueue.id, data: response.data });
              // }
              state.successfullySent = true;
              break;
              // return 'Success';
            } else {
              state.retrySipmovilMessageId = messageInQueue.id;
              state.retrySipmovilMessage = true;
              // messageInQueue.status = 'enqueue';
              // messageInQueue.deleted = false;
              // messageInQueue.created_at = new Date();
              commit("NEW_SIPMOVIL_MESSAGE", messageInQueue);
              console.warn(`Intento ${attempts + 1}: Respuesta no esperada (${response.status}). Reintentando...`);
            }
          } catch (error) {
            if (state.fexIAReceivingResponse !== true) {
              state.retrySipmovilMessageId = messageInQueue.id;
              state.retrySipmovilMessage = true;
              // messageInQueue.direction = 'out';
              // messageInQueue.status = 'enqueue';
              // messageInQueue.deleted = false;
              // messageInQueue.created_at = new Date();
              commit("NEW_SIPMOVIL_MESSAGE", messageInQueue);          
              console.error(`Intento ${attempts + 1}: Error al enviar mensaje`, error);
            }
          }          
          if (state.fexIAReceivingResponse !== true) {
            attempts++;
            await new Promise((resolve) => setTimeout(resolve, 1000));
          } else {
            break;
          }
        }
        attempts = 0;
        state.retrySipmovilMessage = false;
        if (state.successfullySent) { continue }     
        commit("UPDATE_SIPMOVIL_MESSAGE_PARAM", {
          param: 'status', id:messageInQueue.id, value: 'failed'
        });
        commit("UPDATE_SIPMOVIL_MESSAGE_PARAM", {
          param: 'failed_status',
          id: messageInQueue.id,
          value: 'No se pudo enviar el mensaje.'
        });
        console.error("Error: No se pudo enviar el mensaje después de múltiples intentos.");
      }
      state.sendingSipmovilMessage = false;
    },
    async newFexIASipmovilChatMessage({ state, commit, dispatch, rootState }, data) {
      const chunkMessage = state.messages[state.messages.length - 1].data.filter((x) => x.id === data.temporal_id)[0];

      if (!chunkMessage) {
        const message = data;
        message.id = data.temporal_id;
        const date = new Date();
        message.timestamp = date.toISOString();
        message.created_at = message.timestamp;
        message.message = message.chunk;
        message.author = ['Bot', 'Bot'];
        message.kind = 'NO_ATTACHED';
        message.deleted = false;
        message.edited = false;
        message.replyMsj = null;
        message.payload = {
          type: "NO_ATTACHED",
          text: message.chunk
        };
        message.direction = 'in';
        if (rootState.chat.openedChat.chatId == data.chatId) {
          commit('NEW_SIPMOVIL_MESSAGE', message);
        }
      } else {
        if (data.chunk !== "[DONE]") {
          chunkMessage.message += data.chunk;
          chunkMessage.payload.text += data.chunk;
        }
      }

      if (data.chunk === "[DONE]") {
        state.fexIAReceivingResponse = false;
        state.messages[state.messages.length - 1].data.splice(-1, 1);
        data.chat_id = data.chatId;
        data.message.direction = 'in';
        data.message.author[0] = 'Bot';
        dispatch('newSipmovilChatMessage', data);
        return;
      }
    },  
    async forwardSipmovilMessageAction({ state }, formData) {
      const response = await ConversationService.forwardSipmovilMessage(formData)
      state.status = response.status;
      if (response.status === 201 && response.data) {
        return 'Success';
      }
    },
    async editSipmovilMessageAction({ state }, formData) {
      const response = await ConversationService.editSipmovilMessage({ datos: formData })
      state.status = response.status;
      if (response.status === 200) {
        return 'Success';
      }
    },
    async deleteSipmovilMessageAction({ state }, formData) {
      console.log(state.page);
      const response = await ConversationService.deleteSipmovilMessage({ datos: formData })
      if (response.status === 200) {
        return 'Success';
      }
    },
    async readSipmovilMessageAction({ state, commit }, data) {
      // Primero: el usuario debe estar en esta pestaña (visible y con foco). Si no,
      // el leído se aplaza hasta que vuelva; el contador de no leídos queda como está
      // y lo limpia el update_unread que el backend emite tras el POST.
      if (!isTabActive()) {
        commit('SET_PENDING_READ', data);
        return 'Deferred';
      }
      if (state.pendingRead && state.pendingRead.chatId == data.chatId) {
        commit('SET_PENDING_READ', null);
      }
      const response = await ConversationService.readSipmovilMessage({ datos: data })
      state.status = response.status
      if (response.status === 200) {
        return 'Success';
      }
    },
    // Envía el leído aplazado al volver a la pestaña, sólo si ese chat sigue abierto
    // (en el módulo o en el widget). Si se cerró o se cambió de chat, se descarta.
    flushPendingRead({ state, commit, dispatch, rootState }) {
      const pending = state.pendingRead;
      if (!pending || !isTabActive()) return;
      commit('SET_PENDING_READ', null);
      if (rootState.chat.openedChat?.chatId != pending.chatId) return;
      return dispatch('readSipmovilMessageAction', pending);
    },
    // task_reacciones: registra/actualiza/quita la reacción de emoji sobre un mensaje.
    // Backend: POST api/chat/react_message/ { message_id, emoji } (el usuario sale de
    // la sesión; no se manda). Mismo emoji → toggle-off; emoji distinto → reemplaza.
    // Flujo: 1) update OPTIMISTA local para feedback inmediato; 2) POST; 3) sincroniza
    // con la lista autoritativa de la respuesta. El backend además emite el evento
    // socket 'message_reaction' a todos los participantes (ver realtime.js), que
    // vuelve a aplicar SET_MESSAGE_REACTIONS (idempotente).
    async reactToMessageAction({ commit, rootState }, payload) {
      const { messageId, emoji } = payload;
      const userInfo = rootState.auth_store.userInfo || {};
      commit('SET_MESSAGE_REACTION', {
        messageId,
        emoji,
        userId: userInfo.User_id,
        username: userInfo.Username,
      });
      try {
        const response = await ConversationService.reactSipmovilMessage({
          message_id: messageId,
          emoji,
        });
        if (response && response.data && Array.isArray(response.data.reactions)) {
          commit('SET_MESSAGE_REACTIONS', { messageId, reactions: response.data.reactions });
        }
        return 'Success';
      } catch (e) {
        console.error('[reacciones] no se pudo registrar la reacción', e);
      }
    },
    // Reacción del agente en join: se envía como mensaje a WhatsApp con payload
    // { type: 'reaction', msgId, emoji }. Mismo emoji que ya tenía → emoji '' (quitar).
    // Optimista: se pinta ya; el eco del socket (newMessage) la vuelve a aplicar.
    async reactToJoinMessageAction({ commit, dispatch, rootState }, { message, emoji }) {
      const msgId = joinReactionTargetId(message);
      const chatId = rootState.chat.openedChat.id;
      const currentChat = rootState.chat.chats.find((x) => x.id === chatId);
      if (!msgId || !currentChat) return 'Error';
      const user = encryptStorage.getItem('user');
      const current = (message.reactions || []).find(
        (r) => r.user_id === JOIN_BUSINESS_REACTION_ID
      );
      const prevEmoji = current ? current.emoji : '';
      const payload = { type: 'reaction', msgId, emoji: prevEmoji === emoji ? '' : emoji };
      commit('ADD_JOIN_REACTION', {
        payload,
        owner_message: user.Username,
        created_at: new Date().toISOString(),
      });
      try {
        await ConversationService.saveOfficialMessage(
          {
            destination: currentChat.destination,
            app_name: currentChat.app_name,
            owner_message: user.Username,
            payload,
          },
          user.join.tokenJoin
        );
        return 'Success';
      } catch (error) {
        console.error('[reacciones] no se pudo enviar la reacción a WhatsApp', error);
        commit('ADD_JOIN_REACTION', {
          payload: { ...payload, emoji: prevEmoji },
          owner_message: current ? current.username : user.Username,
          created_at: new Date().toISOString(),
        });
        dispatch(
          'ui/alert/displayAlert',
          {
            message: 'Error',
            description: 'No se pudo enviar la reacción, por favor intentelo nuevamente',
            alertType: 'error',
          },
          { root: true }
        );
        return 'Error';
      }
    },
    async alertNewTransfer({state}, message) {
      state.message = message
      const options = {
        body: message,
        icon: "notification-logo.png",
      };
      const n = new Notification("Nueva transferencia", options);
      n.close()
      transferAudio.play();
    },
    async getChatData({ commit }, chatId) {
      
      try {
        const response = await ConversationService.getChatData(chatId);
        
        if (response.status === 200) {
          const chatData = response.data.chat_data;
          commit("chat/UPDATE_CHAT_CONTACTS", chatData, { root: true });
          return 'Success';
        }
      } catch (error) {
        console.error('getChatData - ERROR:', error);
        throw error;
      }
    },
    async getAgents({ commit, rootState }) {
      const user = encryptStorage.getItem('user');
      const join = user.join;
      const token = join.tokenJoin;
      const activePbxUsernames = await getActivePbxUsernames();
      // En la transferencia selectiva no hay chat abierto: un agente solo ve
      // sus conversaciones, así que se excluye a sí mismo como destino.
      const excludeSelf = rootState.chat.transferSelectionMode && join.isAdmin !== true;
      const isTransferable = (agent) =>
        !!agent?.username &&
        agent.username !== rootState.chat.openedChat.owner &&
        !(excludeSelf && agent.username === user.Username) &&
        (!activePbxUsernames || activePbxUsernames.has(agent.username));
      return ConversationService.getAvalaibleAgents(token)
        .then((resp) => {
          const { data } = resp;
          const agents = [];
          for (let i = 0; i < data.length; i++) {
            const grp = data[i];
            if (!grp || !grp.name || !Array.isArray(grp.accounts)) continue;
            for (let j = 0; j < grp.accounts.length; j++) {
              const agent = grp.accounts[j];
              if (isTransferable(agent)) {
                const agentObj = {
                  name: `${agent.name} - ${grp.name}`,
                  image: `https://${user.PbxServer}${agent.avatar}`,
                  account: { agent },
                  groupName: grp.name,
                  userName: agent.username,
                  status: agent.status,
                };
                agents.push(agentObj);
              }
            }
          }
          agents.sort((a, b) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
            if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
            return 0; // Si son iguales
          });
          commit('SET_AGENTS_GROUP', agents);

          // Estructura agrupada por grupo (acordeones del modal de transferencia).
          // Conserva el orden de grupos del backend; agentes ordenados A-Z; grupos vacios omitidos.
          const groupedAgents = [];
          for (let i = 0; i < data.length; i++) {
            const grp = data[i];
            if (!grp || !grp.name || !Array.isArray(grp.accounts)) continue;
            const groupAgents = grp.accounts
              .filter(isTransferable)
              .map((agent) => ({
                name: agent.name,
                image: `https://${user.PbxServer}${agent.avatar}`,
                account: { agent },
                groupName: grp.name,
                userName: agent.username,
                status: agent.status,
              }))
              .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            if (groupAgents.length > 0) {
              groupedAgents.push({
                name: grp.name,
                slug_name: grp.slug_name || grp.name,
                agents: groupAgents,
              });
            }
          }
          commit('SET_AGENTS_GROUPED', groupedAgents);
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
          return 'Error';
        });
    },
    async getStatusAgents({ state, commit }) {
      const user = encryptStorage.getItem('user')
      const join = user.join
      const token = join.tokenJoin;
      const activePbxUsernames = await getActivePbxUsernames();
      return ConversationService.getStatusAgents(token)
        .then((resp) => {
          const { data } = resp;
          const agents = [];
          for (let i = 0; i < data.length; i++) {
            const agent = data[i];
            if (activePbxUsernames && !activePbxUsernames.has(agent.username)) continue;
            const agentObj = {
              name: `${agent.name}`,
              image: `https://${user.PbxServer}${agent.avatar}`,
              account: { agent },
              userName: agent.username,
              status: agent.status,
              totalChats: agent.agent_chats,
              lastMessage: agent.chats_no_last_message,
              page: state.transferAgents.length > 0 ? state.transferAgents.filter((x) => x.userName === agent.username)[0].page : 1,
              filteredCursor: state.transferAgents.length > 0 ? state.transferAgents.filter((x) => x.userName === agent.username)[0].filteredCursor : null,
            };
            agents.push(agentObj);
          }
          agents.sort((a, b) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
            if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
            return 0; // Si son iguales
          });
          commit('SET_AGENTS', agents);
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
          return 'Error';
        });
    },
    async setAgentLastMessage({ state }, { owner, direction }) {
      const agent = state.transferAgents.filter((x) => x.userName === owner)[0];
      if (agent) {
        agent.lastMessage = direction === 'in' ? agent.lastMessage + 1 : agent.lastMessage - 1;
      }
    },
    async setAgentChats({ state }, { owner, add }) {
      const agent = state.transferAgents.filter((x) => x.userName === owner)[0];
      if (agent) {
        agent.totalChats = add ? agent.totalChats + 1 : agent.totalChats - 1;
      }
    },
    handleMessagesRead({ commit, rootState }, { chat_id, reader_id }) {
      const openedChat = rootState.chat.openedChat;
      if (openedChat?.chatId != chat_id) return;
      const currentUsername = rootState.auth_store.userInfo?.Username;
      if (!currentUsername) return;
      commit('UPDATE_MESSAGE_READED_BY', { readerId: reader_id, currentUsername });
      // Si con este lector algún mensaje mío quedó leído por TODOS los activos, fijarlo
      // como leído (sticky) para que no revierta al agregar miembros nuevos después.
      if (openedChat.isGroup) {
        const myId = String(rootState.auth_store.userInfo?.User_id);
        const activeIds = (openedChat.contacts || [])
          .filter((c) => c && c.active !== false)
          .map((c) => String(c.id))
          .filter((id) => id !== myId);
        commit('MARK_ALL_READ_STICKY', { activeIds, currentUsername });
      }
    },
    // FEX-1124: el servidor confirmó que almacenó el mensaje 1-on-1 → ✓ (sent)
    // y entrega el id real de BD para reemplazar el id temporal del bubble.
    handleMessageSent({ commit, rootState }, data) {
      if (rootState.chat.openedChat?.chatId != data.chat_id) return;
      commit('SET_MESSAGE_SENT', {
        tempId: data.id_new_message,
        messageId: data.message_id,
      });
    },
    // FEX-1124: el servidor falló al almacenar el mensaje 1-on-1 → marcar error.
    handleMessageSendError({ commit, rootState }, data) {
      if (rootState.chat.openedChat?.chatId != data.chat_id) return;
      commit('SET_MESSAGE_FAILED', { tempId: data.id_new_message });
    },
    // FEX-1124: el destinatario recibió el/los mensaje(s) → ✓✓ negro. El backend puede
    // mandar un `message_id` (individual) o `message_ids` (lote, al reconectarse el
    // destinatario). No trae chat_id en el lote; solo actualizamos los que estén
    // cargados en la conversación abierta (el resto se reflejará al recargar por el
    // campo `delivered`).
    handleMessageDelivered({ commit, rootState }, data) {
      const ids = Array.isArray(data.message_ids)
        ? data.message_ids
        : (data.message_id !== undefined && data.message_id !== null ? [data.message_id] : []);
      if (!ids.length) return;
      // Solo tocar los mensajes en pantalla (state.messages) si hay un chat SipMóvil abierto.
      // Evita que un evento sipmovil afecte, por coincidencia de id, a mensajes de Join que
      // estén cargados en state.messages. El listado (SET_LAST_MESSAGE_DELIVERED) opera sobre
      // sipmovilChats, así que es seguro correrlo siempre.
      if (rootState.chat.originOpenedChat === 'sipmovil') {
        commit('SET_MESSAGE_DELIVERED', {
          ids,
          delivered: data.delivered,
          deliveredBy: data.delivered_by,
        });
      }
      // Reflejar el ✓✓ también en el listado si el mensaje entregado es el
      // último del chat (se busca por idLastMessage en ambas listas).
      commit('chat/SET_LAST_MESSAGE_DELIVERED', { ids }, { root: true });
    },
    async getSipmovilMessages({ state, commit, rootState }, chatId) {

      const MAX_RETRIES = 5;
      const TIMEOUT_MS = 1000;

      let attempts = 0;
      const fetchWithTimeout = async () => {
        attempts += 1;
        
        const source = axios.CancelToken.source();

        const timer = setTimeout(() => {
          source.cancel(`Timeout: Request took more than ${TIMEOUT_MS}ms`);
        }, TIMEOUT_MS);

        try {
          const response = await ConversationService.getSipmovilMessages(
            chatId,
            state.page,
            { cancelToken: source.token }
          );
          if (response.status !== 200) {
            throw new Error(`Unexpected response status: ${response.status}`);
          }
          clearTimeout(timer);
          commit("LOAD_SIPMOVIL_MESSAGES", processSipmovilMessages(response.data.messages, true, rootState.chat.openedChat.isFexIA || false));
          if (response.data.membership_periods !== undefined) {
            commit("SET_MEMBERSHIP_PERIODS", response.data.membership_periods);
          }
          if (response.data.is_active_member !== undefined) {
            commit("SET_ACTIVE_MEMBER", response.data.is_active_member);
            if (response.data.exit_reason !== undefined) {
              commit("SET_EXIT_REASON", response.data.exit_reason ?? null);
            }
          }
          commit('SET_STATUS', false);
          if (state.page === response.data.pagination.total_pages) {
            commit("chat/SET_SIPMOVIL_LAST", true, { root: true });
          } else {
            state.page = response.data.pagination.current_page + 1;
          }
          return 'Success';
        } catch (error) {
          clearTimeout(timer);

          if (axios.isCancel(error) || axios.isAxiosError(error)) {
            console.warn(`Attempt ${attempts}: ${error.message}`);
            if (attempts < MAX_RETRIES) {
              return await fetchWithTimeout(); // Retry
            } else {
              commit('SET_STATUS', false);
              throw new Error('Request failed after 5 retries due to timeout');
            }
          } else {
            console.error('Axios error:', error);
            commit('SET_STATUS', false);
            throw error;
          }
        }
      };

      return fetchWithTimeout();

    },
    async getMessagesFrom({ state, commit, rootState }, {chatId, messageId}) {
      return ConversationService.getMessagesFrom(chatId, messageId, state.page)
        .then((response) => {
          commit("LOAD_SIPMOVIL_MESSAGES_FROM", {messages: processSipmovilMessages(response.data.results, true, rootState.chat.openedChat.isFexIA || false), reset: true});
          commit('SET_STATUS', false);
          if (response.data.page) {
            state.page = response.data.page + 1;
          }
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async getFilteredMessages({ commit, rootState }, chatData) {
          return ConversationService.getFilteredMessages(chatData)
            .then((response) => {
              commit("LOAD_SIPMOVIL_MESSAGES", processSipmovilMessages(response.data.messages.reverse(), false, rootState.chat.openedChat.isFexIA || false));
              commit("SET_STATUS", false);
              commit("chat/LOAD_SIPMOVIL_CHATS", rootState.chat.renderSipmovilChats, { root: true })
              rootState.chat.stringfilter = '';
              if (response.data.next !== null) {
                commit("UPDATE_PAGE", response.data.next);
              } else {
                commit("UPDATE_PAGE", 0);
              }
              commit("chat/UPDATE_SIPMOVIL_FOUNDED_CHATS", [], { root: true });
              return;
            })
            .catch((error) => {
              console.log(error);
            });
        },
    async fetchSenderMessages({ state, commit }, chatId) {
      commit('SET_STATUS', true);
      const join = encryptStorage.getItem('user').join
      const token = join.tokenJoin;

      const MAX_RETRIES = 10;
      const TIMEOUT_MS = 1000;

      let attempts = 0;

      const fetchWithTimeout = async () => {
        attempts += 1;

        const source = axios.CancelToken.source();

        const timer = setTimeout(() => {
          source.cancel(`Timeout: Request took more than ${TIMEOUT_MS}ms`);
        }, TIMEOUT_MS);

        try {
          const response = await ConversationService.getSenderMessages(
            chatId,
            state.page,
            token,
            { cancelToken: source.token }
          );

          if (response.status !== 200) {
            throw new Error(`Unexpected response status: ${response.status}`);
          }

          clearTimeout(timer);

          console.log('retrieving message for page ', state.page, response);
          commit('LOAD_MESSAGES', processMessages(response.data.results));
          commit('SET_STATUS', false);

          if (response.data.next !== null) {
            state.page += 1;
            console.log('There are more messages to load. Next page: ', state.page);
          } else {
            console.log('No more messages to load');
            state.page = 0;
          }

          return 'Success';

        } catch (error) {
          clearTimeout(timer);

          if (axios.isCancel(error) || axios.isAxiosError(error)) {
            console.warn(`Attempt ${attempts}: ${error.message}`);
            if (attempts < MAX_RETRIES) {
              return await fetchWithTimeout(); // Retry
            } else {
              commit('SET_STATUS', false);
              throw new Error('Request failed after 5 retries due to timeout');
            }
          } else {
            console.error('Axios error:', error);
            commit('SET_STATUS', false);
            throw error;
          }
        }
      };

      return fetchWithTimeout();

      // return ConversationService.getSenderMessages(chatId, state.page, token)
      //   .then((response) => {
      //     console.log('retriving message for page ', state.page, response);
      //     commit('LOAD_MESSAGES', processMessages(response.data.results));
      //     commit('SET_STATUS', false);
      //     if (response.data.next !== null) {
      //       state.page += 1;
      //       console.log(
      //         'There are more message to load next page : ',
      //         state.page
      //       );
      //     } else {
      //       console.log('No more message to load');
      //       state.page = 0;
      //     }
      //     return 'Success';
      //   })
      //   .catch((error) => {
      //     console.log(error);
      //   });
    },
    async fetchContextMessage({ state }, context) {
      const join = encryptStorage.getItem('user').join
      const token = join.tokenJoin;
      return ConversationService.getSenderMessage(context, token)
        .then((response) => {
          console.log(state.page);
          return response.data;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    newEventMessage({ commit }, message) {
      commit('UPDATE_MESSAGE_STATE', message);
    },
    markAsReadMessages({ state, rootState }, {data, status = 'read'}) {
      const chatIndex = rootState.chat.sipmovilChats.findIndex((x) => x.chatId == data.chat_id, -1);
      const chat = rootState.chat.sipmovilChats[chatIndex];
      if (chat !== undefined) {
        if (rootState.auth_store.userInfo.User_id !== data.idUserSendMsj) {
          chat.last_message_status = status
        }else{
          chat.unread = 0;
        }
        // chat.last_message_incoming = true
        
        if (chat.chatId === rootState.chat.openedChat.chatId) {
          const currentUserId = String(rootState.auth_store.userInfo.User_id);
          const isCurrentUserTriggered = currentUserId === String(data.idUserSendMsj);
          state.messages.forEach(element => {
            element.data.forEach(itemData => {
              const isSentByMe = String(itemData.id_user_send) === currentUserId;
              if (isCurrentUserTriggered && !isSentByMe) {
                // Yo entré al chat → solo marcar mensajes que yo recibí
                itemData.readedMsj = true;
                itemData.status = status;
              } else if (!isCurrentUserTriggered && isSentByMe) {
                // Otra persona leyó → solo marcar mis mensajes enviados
                itemData.readedMsj = true;
                itemData.status = status;
              }
            })
          })
        }
      }
    },
    async fetchVariablesTemplates({ commit, state }, templateId) {
      commit('SET_IS_LOADING_DEFAULT_MESSAGES', true);
      const token = encryptStorage.getItem('token');
      
      try {
        const response = await TemplatesServices.getVariablesTemplates(token, templateId);
        const backendVars = response.data || {};
        const updates = {};
        Object.keys(state.messagePlaceHolder).forEach(key => {
          if (backendVars[key] !== undefined) {
            updates[key] = backendVars[key];
          }
        });
        commit('SET_PLACEHOLDERS', {
          ...state.messagePlaceHolder,
          ...updates
        });
        commit('SET_PARAMS_TEMPLATE_PBX', updates);
        commit('SET_PLACEHOLDER_BACKEND', backendVars);
        return 'Success';
      } catch (error) {
        console.error('Error fetching template variables:', error);
        throw error;
      } finally {
        commit('SET_IS_LOADING_DEFAULT_MESSAGES', false);
      }
    },
    async setVariablesTemplates({ commit }, data) {
      commit('SET_IS_LOADING_DEFAULT_MESSAGES', true);
      const token = encryptStorage.getItem('token');
      try {
        const response = await TemplatesServices.updateVariablesTemplates(token, data);
        return response;
      } catch (error) {
        console.log(error);
        throw error;
      } finally {
        commit('SET_IS_LOADING_DEFAULT_MESSAGES', false);
      }
    },
  },
};

export default conversation;
